import * as React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Card, Label, Loader, Table, useNotify } from 'ebs-design';
import { Pagination } from 'components/organisms';
import { useQueryParams, useQueryUpdate } from 'hooks/useQueryParams';
import { ActionHistoryFilter } from 'features/actionHistory/Filters/ActionHistoryFilter';
import { defaultFilters } from 'utils';
import { useLogsAPI } from 'api';
import { dateFormat, dateFormatAPI, dateTimeFormat, format } from 'libs';
import { Column, FilterType, Log, Results } from 'types';

export const ActionHistory: React.FC = () => {
  const { t } = useTranslation();
  const params = useQueryParams();
  const notify = useNotify();
  const { getLogs } = useLogsAPI();
  const { updateQuery } = useQueryUpdate();

  const { data: logs, isLoading } = useQuery<Results<Log>>(
    [
      'action-history-logs',
      {
        ...defaultFilters,
        ...params,
        created_at_start:
          params.created_at_start && format(params.created_at_start, dateFormatAPI, dateFormat),
        created_at_end:
          params.created_at_end && format(params.created_at_end, dateFormatAPI, dateFormat),
        created_at: undefined,
      },
    ],
    getLogs,
    {
      onError: () => {
        notify.error({ title: t('error.someThingIsWrong') });
      },
    },
  );

  const handleFilter = (type: FilterType, field: string): FilterType => {
    updateQuery({ ...params, ordering: type ? `${type === 'desc' ? '-' : ''}${field}` : type });
    return type;
  };

  const columns: Column<Log>[] = React.useMemo(
    () => [
      {
        title: t('calls.id'),
        onFilter: (type) => handleFilter(type, 'id'),
        dataIndex: 'id',
      },
      {
        title: t('logs.dateTime'),
        onFilter: (type) => handleFilter(type, 'created_at'),
        render: ({ created_at }) => format(created_at, dateTimeFormat),
      },
      {
        title: t('logs.type'),
        onFilter: (type) => handleFilter(type, 'type'),
        render: ({ type }) => <Label text={t(`logs.${type}`)} />,
      },
      {
        title: t('logs.who'),
        onFilter: (type) => handleFilter(type, 'created_by'),
        render: ({ created_by }) =>
          created_by
            ? [created_by.first_name, created_by.last_name].filter(Boolean).join(' ') ||
              created_by.email
            : '---',
      },
      {
        title: t('contacts.contact'),
        render: ({ contact }) => {
          if (!contact) return '---';
          const name = [contact.first_name, contact.last_name].filter(Boolean).join(' ');
          return contact.lead ? (
            <Link to={`/lead/${contact.lead}`}>{name || '---'}</Link>
          ) : (
            <span>{name || '---'}</span>
          );
        },
      },
      {
        title: t('lead.lead'),
        render: ({ lead }) => {
          if (!lead) return '---';
          return <Link to={`/lead/${lead.id}`}>{lead.name || `#${lead.id}`}</Link>;
        },
      },
      {
        title: t('contacts.description'),
        render: ({ description }) => description || '---',
      },
    ],
    [params, t],
  );

  return (
    <div className="action-history">
      <ActionHistoryFilter count={logs?.count} />

      <Card>
        <Loader loading={isLoading} size="regular">
          <Table
            emptyText={t('main.noData')}
            data={logs?.results}
            columns={columns}
            className="no-border"
          />
        </Loader>
        <Card.Footer>
          <Pagination size={logs?.total_pages || 0} />
        </Card.Footer>
      </Card>
    </div>
  );
};
